// Login page with support for NEW_PASSWORD_REQUIRED and MFA TOTP challenge flows.
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router';
import { signIn, confirmSignIn } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';
import { Button, Alert } from '../components/common';
import { useTheme } from '../contexts/ThemeContext';
import logoPrimary from '../assets/contractgen-logo-primary.svg';
import logoPrimaryDark from '../assets/contractgen-logo-primary-dark.svg';
import { authPageWrapper, authCard, authLogoClass } from './authLayout';

type Challenge = 'none' | 'new-password' | 'totp';

export default function Login() {
  const { isAuthenticated, refreshUser } = useAuth();
  const { resolvedTheme } = useTheme();
  const logo = resolvedTheme === 'dark' ? logoPrimaryDark : logoPrimary;
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [challenge, setChallenge] = useState<Challenge>('none');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await signIn({
        username: email.trim(),
        password,
        options: {
          authFlowType: 'USER_PASSWORD_AUTH',
        },
      });

      if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setChallenge('new-password');
        setIsLoading(false);
        return;
      }

      if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setChallenge('totp');
        setIsLoading(false);
        return;
      }

      await refreshUser();
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Sign in error:', err);
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      await confirmSignIn({ challengeResponse: newPassword });
      await refreshUser();
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Set password error:', err);
      setError(err instanceof Error ? err.message : 'Failed to set password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTotpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await confirmSignIn({ challengeResponse: totpCode });
      await refreshUser();
      navigate('/', { replace: true });
    } catch (err) {
      console.error('MFA verification error:', err);
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (challenge === 'totp') {
    return (
      <div className={authPageWrapper}>
        <div className={`${authCard} space-y-8`}>
          <div className="text-center">
            <img src={logo} alt="ContractGen" className={authLogoClass} />
            <h1 className="text-h1 text-slate-900 dark:text-slate-50">
              Two-Factor Authentication
            </h1>
            <p className="mt-2 text-body text-slate-600 dark:text-slate-400">
              Enter the verification code from your authenticator app.
            </p>
          </div>

          <form onSubmit={handleTotpVerify} className="mt-8 space-y-6">
            {error && <Alert variant="error">{error}</Alert>}

            <div>
              <label htmlFor="totp-code" className="label">
                Verification Code
              </label>
              <input
                id="totp-code"
                type="text"
                required
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                className="input"
                placeholder="123456"
                autoComplete="one-time-code"
              />
            </div>

            <Button type="submit" loading={isLoading} className="w-full">
              Verify
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (challenge === 'new-password') {
    return (
      <div className={authPageWrapper}>
        <div className={`${authCard} space-y-8`}>
          <div className="text-center">
            <img src={logo} alt="ContractGen" className={authLogoClass} />
            <h1 className="text-h1 text-slate-900 dark:text-slate-50">Set New Password</h1>
            <p className="mt-2 text-slate-600 dark:text-slate-400">Your account requires a new password</p>
          </div>

          <form onSubmit={handleNewPassword} className="mt-8 space-y-6">
            {error && (
              <Alert variant="error">{error}</Alert>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="label">
                  New Password
                </label>
                <input
                  id="newPassword"
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="label">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <Button type="submit" loading={isLoading} className="w-full">
              Set Password
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={authPageWrapper}>
      <div className={`${authCard} space-y-8`}>
        <div className="text-center">
          <img src={logo} alt="ContractGen" className={authLogoClass} />
          <h1 className="text-h1 text-slate-900 dark:text-slate-50">Sign in</h1>
          <p className="mt-2 text-body text-slate-600 dark:text-slate-400">Sign in to generate and manage your contracts</p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          {error && (
            <Alert variant="error">{error}</Alert>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="••••••••"
              />
            </div>
          </div>

          <Button type="submit" loading={isLoading} className="w-full">
            Sign in
          </Button>

          <div className="text-center">
            <Link
              to="/forgot-password"
              className="text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 font-medium text-body-sm"
            >
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
